import React, { useEffect } from 'react';
import moment from 'moment';
import { Spin } from 'antd';
import { regionsOf, reportStudyOf, visitStatus } from './patientPriors';

// Patient history for one All Studies row: one entry per earlier visit, the
// first line of each signed report's impression, and a way into the report.
// Per-prior "open" buttons are deliberately absent: the viewer already lists
// every study of the patient, so one "Open in viewer" covers them all.

const formatDate = (yyyymmdd) => {
  const m = moment(yyyymmdd, 'YYYYMMDD', true);
  return m.isValid() ? m.format('DD-MMM-YYYY') : yyyymmdd;
};

const ago = (yyyymmdd) => {
  const m = moment(yyyymmdd, 'YYYYMMDD', true);
  return m.isValid() ? m.fromNow() : '';
};

const plural = (n, one, many) => `${n.toLocaleString()} ${n === 1 ? one : many}`;

const visitMeta = (visit) => {
  const parts = [];
  if (visit.studies.length > 1) parts.push(`${visit.studies.length} studies same day`);
  const images = visit.studies.reduce((sum, s) => sum + (s.instance_count || 0), 0);
  if (images > 0) parts.push(plural(images, 'image', 'images'));
  const signers = Array.from(new Set(visit.studies.map((s) => s.report_signer_name).filter(Boolean)));
  if (signers.length > 0) parts.push(`read by ${signers.join(', ')}`);
  return parts.join(' · ');
};

const PatientHistoryPanel = ({ row, visits, relevant, impressions, onClose, onOpenReport, onOpenViewer }) => {
  useEffect(() => {
    const onKey = (e) => { if (e.key === 'Escape') onClose(); };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [onClose]);

  const rowRegions = regionsOf(row.bodyPart, row.studyDescription);
  const regionKnown = relevant !== null;
  const who = [row.patientName, row.patientID && row.patientID !== 'N/A' ? `ID ${row.patientID}` : '',
    [row.sex, row.age].filter((v) => v && v !== 'N/A' && v !== 'U').join('/')].filter(Boolean).join(' · ');
  const current = [formatDate(row.studyDateRaw), row.modality, row.bodyPart !== 'N/A' ? row.bodyPart : '']
    .filter(Boolean).join(' · ');

  return (
    <div className="ph-overlay" onClick={onClose}>
      <div
        className="ph-dialog"
        role="dialog"
        aria-label={`Patient history for ${row.patientName}`}
        onClick={(e) => e.stopPropagation()}
        onDoubleClick={(e) => e.stopPropagation()}
      >
        <div className="ph-header">
          <div className="ph-header-text">
            <span className="ph-title">Patient history · {plural(visits.length, 'prior visit', 'prior visits')}</span>
            <span className="ph-subtitle">{who}</span>
          </div>
          <button type="button" className="ph-close" aria-label="Close" onClick={onClose}>&#10005;</button>
        </div>
        <div className="ph-current">
          <span className="ph-current-label">Current</span>
          <span className="ph-current-value">{current}</span>
          {regionKnown && <span>· same-region priors highlighted</span>}
        </div>
        <div className="ph-body">
          {visits.map((visit) => {
            const first = visit.studies[0];
            const modalities = Array.from(new Set(visit.studies.map((s) => s.modality).filter(Boolean)));
            const description = first.study_description || first.body_part || 'Study';
            const status = visitStatus(visit);
            const reportStudy = reportStudyOf(visit, rowRegions);
            const impression = reportStudy ? impressions[reportStudy.study_instance_uid] : undefined;
            return (
              <div key={visit.date} className={`ph-visit${visit.sameRegion ? ' ph-visit-same' : ''}`}>
                <span className="ph-visit-date">
                  <span className="ph-date">{formatDate(visit.date)}</span>
                  <span className="ph-ago">{ago(visit.date)}</span>
                </span>
                <span className="ph-visit-main">
                  <span className="ph-visit-line">
                    {modalities.length > 0 && <span className="ph-modality">{modalities.join(' / ')}</span>}
                    <span className="ph-desc">{description}</span>
                    {regionKnown && (
                      <span className={`ph-region${visit.sameRegion ? ' ph-region-same' : ''}`}>
                        <span className="ph-dot" />{visit.sameRegion ? 'Same region' : 'Other region'}
                      </span>
                    )}
                  </span>
                  {visitMeta(visit) && <span className="ph-meta">{visitMeta(visit)}</span>}
                  {reportStudy && impression === null && (
                    <span className="ph-impression ph-impression-loading"><Spin size="small" /> Loading impression…</span>
                  )}
                  {reportStudy && impression && (
                    <span className="ph-impression"><span className="ph-impression-label">Impression · </span>{impression}</span>
                  )}
                </span>
                <span className="ph-visit-side">
                  <span className={`ph-status ph-status-${status.tone}`}><span className="ph-dot" />{status.label}</span>
                  {reportStudy && (
                    <button type="button" className="ph-report-btn" onClick={() => onOpenReport(reportStudy)}>
                      Report
                    </button>
                  )}
                </span>
              </div>
            );
          })}
        </div>
        <div className="ph-footer">
          <span className="ph-footer-note">All prior images are in the viewer&apos;s study list</span>
          <button type="button" className="ph-open-btn" onClick={() => onOpenViewer(row)}>Open in viewer</button>
        </div>
      </div>
    </div>
  );
};

export default PatientHistoryPanel;
